import type { GridConfig, GridColumn, GridRow } from '@hcc/types';
import { resolveGridCell, applyMask, maskPlaceholder } from '@hcc/types';
import type { GridValue } from './formValues';

interface GridFieldViewProps {
  grid: GridConfig;
  value: GridValue;
  onChange: (next: GridValue) => void;
  disabled?: boolean;
}

const CELL_CLASS =
  'block w-full rounded-token-sm border border-line-strong/20 bg-surface px-2 py-1.5 text-sm';

/**
 * Renders a generic grid: labelled rows, typed columns (text / number /
 * select). A real table on desktop; on mobile each row stacks into a card with
 * full-width inputs so nothing is pushed off-screen.
 */
export function GridFieldView({ grid, value, onChange, disabled }: GridFieldViewProps) {
  function setCell(rowKey: string, colKey: string, cell: string) {
    onChange({ ...value, [rowKey]: { ...(value[rowKey] ?? {}), [colKey]: cell } });
  }

  // A plain render function, NOT a nested component: rendering it as <Cell/>
  // would create a new component type each render and remount the input on
  // every keystroke (dropping focus and characters). Called inline instead.
  function renderCell(row: GridRow, col: GridColumn) {
    const cellVal = value[row.key]?.[col.key] ?? '';
    // The row may override the column's input (dropdown / mask / number).
    const resolved = resolveGridCell(row, col);
    if (resolved.cell === 'select') {
      return (
        <select
          value={cellVal}
          disabled={disabled}
          aria-label={`${row.label} ${col.label}`}
          onChange={(e) => setCell(row.key, col.key, e.target.value)}
          className={CELL_CLASS}
        >
          <option value="">Select…</option>
          {(resolved.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      );
    }
    if (resolved.cell === 'checkbox') {
      return (
        <input
          type="checkbox"
          checked={cellVal === 'yes'}
          disabled={disabled}
          aria-label={`${row.label} ${col.label}`}
          onChange={(e) => setCell(row.key, col.key, e.target.checked ? 'yes' : '')}
          className="h-5 w-5 accent-accent"
        />
      );
    }
    const mask = resolved.cell === 'number' || resolved.cell === 'text' ? resolved.mask : undefined;
    return (
      <input
        // A masked value can carry literals/spaces, so it stays a text box even
        // for a number cell; the keypad hint stays numeric.
        type={resolved.cell === 'number' && !mask ? 'number' : 'text'}
        inputMode={resolved.cell === 'number' ? 'numeric' : undefined}
        value={cellVal}
        disabled={disabled}
        placeholder={mask ? maskPlaceholder(mask) : undefined}
        aria-label={`${row.label} ${col.label}`}
        onChange={(e) => setCell(row.key, col.key, mask ? applyMask(mask, e.target.value) : e.target.value)}
        className={CELL_CLASS}
      />
    );
  }

  return (
    <div className="mt-1.5">
      {/* Desktop: table */}
      <div className="hidden overflow-x-auto rounded-token-sm border border-line/10 sm:block">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-surface-2 text-left text-xs text-fg-muted">
              <th className="px-3 py-2 font-medium" />
              {grid.columns.map((c) => (
                <th key={c.key} className="px-3 py-2 font-medium">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.rows.map((row) => (
              <tr key={row.key} className="border-t border-line/10">
                <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-fg-muted">
                  {row.label}
                </th>
                {grid.columns.map((col) => (
                  <td key={col.key} className="px-2 py-1.5">
                    {renderCell(row, col)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked cards */}
      <div className="space-y-3 sm:hidden">
        {grid.rows.map((row) => (
          <div key={row.key} className="rounded-token-sm border border-line/10 bg-surface-2 p-3">
            <div className="mb-2 text-sm font-medium text-fg">{row.label}</div>
            <div className="space-y-2">
              {grid.columns.map((col) => (
                <div key={col.key}>
                  {grid.columns.length > 1 ? (
                    <div className="mb-1 text-xs text-fg-muted">{col.label}</div>
                  ) : null}
                  {renderCell(row, col)}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
